import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2, AlertTriangle, XCircle, Loader2, Image, ArrowRight } from "lucide-react";

export default function AdminMediaImport() {
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [report, setReport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get("/admin/media/stats"), api.get("/admin/media/imports")]);
      setStats(s.data); setLog(l.data.imports || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const pick = (f) => { setFile(f); setReport(null); setResult(null); };

  const validate = async () => {
    if (!file) return;
    setValidating(true); setReport(null); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/media/validate", fd);
      setReport(r.data);
      toast[r.data.valid ? "success" : "error"](r.data.valid ? "Validation passed" : "Validation found blocking errors");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally { setValidating(false); }
  };

  const runImport = async () => {
    if (!file || !report?.valid) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/media/import", fd);
      setResult(r.data.import);
      toast.success(`Media imported — ${r.data.import.upserts} rows updated`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Import failed");
    } finally { setImporting(false); }
  };

  const pct = (n) => stats?.totalColleges ? Math.round((n / stats.totalColleges) * 100) : 0;

  return (
    <div data-testid="admin-media-import-page" className="space-y-6 max-w-5xl">
      <SEOHead title="Media Import" />
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Image className="h-6 w-6 text-[#0B2A6B]" /> Master Media Engine
        </h1>
        <p className="text-sm text-slate-500">Upload the <b>CollegeUnlock_Master_Media_Database.csv</b>. It joins to colleges by <b>College_ID</b> and updates only media fields (logos, images, and any future columns). MID, leads, CRM and search are never touched.</p>
      </div>

      {/* Coverage stats */}
      {stats && (
        <div data-testid="media-stats" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ["Total Colleges", stats.totalColleges, null],
            ["Media Records", stats.mediaRecords, null],
            ["With Logo", `${stats.withLogo}`, `${pct(stats.withLogo)}%`],
            ["With Image", `${stats.withImage}`, `${pct(stats.withImage)}%`],
            ["Missing Logos", stats.missingLogo, null],
            ["Missing Images", stats.missingImage, null],
          ].map(([k, v, sub]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="font-display text-2xl font-extrabold text-slate-900">{v ?? 0}</p>
              <p className="text-xs text-slate-500">{k}{sub ? ` · ${sub}` : ""}</p>
            </div>
          ))}
        </div>
      )}
      {stats?.lastImport && (
        <p className="text-xs text-slate-400">Last import: <b>{stats.lastImport.filename}</b> · {new Date(stats.lastImport.importedAt).toLocaleString()} · {stats.lastImport.upserts} rows</p>
      )}

      <div
        data-testid="media-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
        className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 text-center transition-colors hover:border-[#0B2A6B]"
      >
        <input ref={inputRef} type="file" accept=".csv" hidden data-testid="media-file-input"
          onChange={(e) => pick(e.target.files?.[0])} />
        <UploadCloud className="mx-auto h-10 w-10 text-[#0B2A6B]" />
        {file ? (
          <p className="mt-3 inline-flex items-center gap-2 font-medium text-slate-800"><FileText className="h-4 w-4 text-emerald-600" /> {file.name}</p>
        ) : (
          <p className="mt-3 text-slate-500">Click or drag a <span className="font-semibold">.csv</span> media file here</p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button data-testid="media-validate-btn" onClick={validate} disabled={!file || validating}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-slate-700">
          {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Validate
        </button>
        <button data-testid="media-import-btn" onClick={runImport} disabled={!report?.valid || importing}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-[#081E4D]">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Import Media
        </button>
      </div>

      {result && (
        <div data-testid="media-import-result" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Media import complete — {result.upserts} colleges updated</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {result.columns?.map((col) => <span key={col} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700">{col}</span>)}
          </div>
        </div>
      )}

      {report && (
        <div className="space-y-4" data-testid="media-validation-report">
          <div className={`rounded-xl border p-4 ${report.valid ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
            {report.valid
              ? <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Validation passed — ready to import</p>
              : <p className="flex items-center gap-2 font-semibold text-red-700"><XCircle className="h-5 w-5" /> {report.errors.length} blocking error(s)</p>}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Rows", report.total], ["Matched to MID", report.matched], ["Unmatched", report.unmatchedCount], ["Duplicates", report.duplicates]].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"><p className="font-display text-2xl font-extrabold text-slate-900">{v ?? 0}</p><p className="text-xs text-slate-500">{k}</p></div>
            ))}
          </div>

          {report.columns?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-bold text-slate-900">Media columns detected</h3>
              <div className="flex flex-wrap gap-2">
                {report.columns.map((col) => <span key={col} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{col}</span>)}
              </div>
            </div>
          )}

          {report.errors?.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-white p-4">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-red-700"><XCircle className="h-4 w-4" /> Errors</h3>
              <ul className="space-y-1 text-sm text-slate-700">{report.errors.map((e, i) => <li key={i} className="flex gap-2"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />{e}</li>)}</ul>
            </div>
          )}
          {report.warnings?.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-white p-4">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-700"><AlertTriangle className="h-4 w-4" /> Warnings</h3>
              <ul className="space-y-1 text-sm text-slate-600">{report.warnings.map((w, i) => <li key={i} className="flex gap-2"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />{w}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {/* Import log */}
      {log.length > 0 && (
        <div data-testid="media-import-log" className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-bold text-slate-900">Import Log</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-400"><tr>
                <th className="py-1 pr-3">Date</th><th className="py-1 pr-3">File</th><th className="py-1 pr-3">Rows</th>
                <th className="py-1 pr-3">Imported</th><th className="py-1 pr-3">Matched</th><th className="py-1 pr-3">Unmatched</th><th className="py-1">By</th>
              </tr></thead>
              <tbody>
                {log.map((it) => (
                  <tr key={it.id || it.importedAt} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-600">{new Date(it.importedAt).toLocaleString()}</td>
                    <td className="py-1.5 pr-3 font-medium text-slate-700">{it.filename}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{it.rows}</td>
                    <td className="py-1.5 pr-3 text-emerald-600 font-medium">{it.upserts}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{it.matched ?? "—"}</td>
                    <td className="py-1.5 pr-3 text-amber-600">{it.unmatched ?? "—"}</td>
                    <td className="py-1.5 text-slate-500">{it.importedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
